import { Minus, Plus, X } from "lucide-react";
import productPlaceholder from "../../assets/product-placeholder.svg";
import { formatCurrency } from "../../lib/productUtils";

const productImageFallback = productPlaceholder;

/**
 * DrawerInCart — shows existing cart configurations for a configurable product.
 *
 * Opened when:
 *  - Row tap on a configurable product that already has lines in cart
 *  - "Scegli" button on a configurable product already in cart
 *
 * Contains:
 *  - List of existing cart lines with image, description, price, qty stepper, "Modifica"
 *  - "Aggiungi" primary CTA at the bottom → opens DrawerProductDetail (fresh configure)
 */
export function DrawerInCart({
  product,
  productLines,   // CartLine[] for this product
  primaryColor,
  onClose,
  onEditLine,     // (line: CartLine) => void
  onChangeLineQty, // (line: CartLine, delta: number) => void
  onAddNew,       // () => void — opens fresh configure view
}) {
  if (!product) return null;

  return (
    <div className="flex max-h-[86vh] flex-col">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="shrink-0 border-b border-[#e2e8f0] px-5 pb-4 pt-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-base font-semibold text-[#0f172a]">{product?.name || "Prodotto"}</p>
            <p className="mt-0.5 text-xs text-[#64748b]">
              {productLines.length
                ? `${productLines.length} configurazion${productLines.length > 1 ? "i" : "e"} nel carrello`
                : "Nessuna configurazione nel carrello."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f1f5f9] text-[#64748b]"
            aria-label="Chiudi"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ── Configuration list ─────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto px-5 py-4">
        {productLines.length ? (
          <div className="space-y-3">
            {productLines.map((line) => {
              const detailText =
                [
                  line.variantLabel,
                  ...(Array.isArray(line.optionSummaries)
                    ? line.optionSummaries.map((e) => `${e.label}: ${e.value}`)
                    : []),
                ]
                  .filter(Boolean)
                  .join(" • ") || "Configurazione base";

              return (
                <div
                  key={line.id}
                  className="rounded-xl border border-[#e2e8f0] bg-white p-3"
                >
                  {/* Image + description */}
                  <div className="flex items-start gap-3">
                    <img
                      src={line.imageUrl || productImageFallback}
                      alt={line.productName}
                      className="h-12 w-12 shrink-0 rounded-lg object-cover"
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = productImageFallback; }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-5 text-[#0f172a]">{detailText}</p>
                      {Number(line.unitPrice) > 0 ? (
                        <p className="mt-1 text-xs font-semibold text-[#020617]">{formatCurrency(line.unitPrice)}</p>
                      ) : null}
                    </div>
                  </div>

                  {/* Qty stepper + Modifica */}
                  <div className="mt-3 flex items-center justify-between">
                    {/* Stepper — ProductDetail.jsx style */}
                    <div className="inline-flex h-9 items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white px-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <button
                        type="button"
                        onClick={() => onChangeLineQty(line, -1)}
                        className="inline-flex h-5 w-5 items-center justify-center rounded text-[#334155]"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="min-w-5 text-center text-sm font-semibold text-[#020617]">
                        {line.quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => onChangeLineQty(line, 1)}
                        className="inline-flex h-5 w-5 items-center justify-center rounded text-[#334155]"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => onEditLine(line)}
                      className="text-xs font-semibold"
                      style={{ color: primaryColor }}
                    >
                      Modifica
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-[#64748b]">Nessuna configurazione nel carrello.</p>
        )}
      </div>

      {/* ── "Aggiungi" CTA ─────────────────────────────────────────────────── */}
      <div className="shrink-0 border-t border-[#e2e8f0] px-5 pb-10 pt-3">
        <button
          type="button"
          onClick={onAddNew}
          className="flex h-11 w-full items-center justify-center rounded-lg text-sm font-semibold text-white"
          style={{ backgroundColor: primaryColor }}
        >
          Aggiungi
        </button>
      </div>
    </div>
  );
}
